import { CircleArrowUpFill, Eye, EyeSlash } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import classNames from 'classnames';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import InputField, { fieldButtonHitAreaClass } from '../InputField';
import type { Props as InputFieldProps } from '../InputField';

type Props = Omit<InputFieldProps, 'type' | 'suffix' | 'isSuffixFocusVisible'>;

// Both glyphs stay mounted and cross-fade, so the swap reads as one control changing state.
const iconSwapClass =
  'transition-[opacity,scale,filter] duration-(--duration-fast) ease-(--ease-out-strong) motion-reduce:transition-opacity';
// From 0.9, not near zero: a glyph that grows out of nothing reads as appearing from nowhere.
const iconHiddenClass = 'scale-90 opacity-0 blur-(--blur-small)';

const PasswordInputField = ({ className, onKeyUp, onKeyDown, onBlur, ...props }: Props) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);

  const detectCapsLock = (event: KeyboardEvent<HTMLInputElement>) => {
    setIsCapsLockOn(event.getModifierState('CapsLock'));
  };

  return (
    <div className={classNames('flex w-full flex-col gap-1', className)}>
      <InputField
        // A revealed password is a plain text field, which mobile keyboards would otherwise
        // capitalise and autocorrect.
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        {...props}
        type={showPassword ? 'text' : 'password'}
        suffix={
          <Button
            isIconOnly
            // Keeps focus (and the caret) in the input while toggling.
            preventFocusOnPress
            size="sm"
            variant="ghost"
            // The name stays constant while `aria-pressed` carries the state; a name that
            // flips to "Hide password" reads to assistive tech as a different control.
            aria-label={t('action.show_password')}
            aria-pressed={showPassword}
            className={classNames('text-muted', fieldButtonHitAreaClass)}
            onPress={() => {
              setShowPassword((previous) => !previous);
            }}
          >
            <span className="grid place-items-center *:[grid-area:1/1]">
              <Eye className={classNames(iconSwapClass, !showPassword && iconHiddenClass)} />
              <EyeSlash className={classNames(iconSwapClass, showPassword && iconHiddenClass)} />
            </span>
          </Button>
        }
        onKeyUp={(event) => {
          detectCapsLock(event);
          onKeyUp?.(event);
        }}
        onKeyDown={(event) => {
          detectCapsLock(event);
          onKeyDown?.(event);
        }}
        onBlur={(event) => {
          setIsCapsLockOn(false);
          onBlur?.(event);
        }}
      />
      {/* Always mounted: a live region that mounts with its text is often not announced.
          `absolute` (not `hidden`) while empty keeps it out of the flex gap but in the a11y tree. */}
      <div role="status" aria-live="polite" className="empty:absolute">
        {isCapsLockOn && (
          <div className="flex items-center gap-1.5 text-xs text-warning-soft-foreground animate-in fade-in duration-(--duration-quick) motion-reduce:animate-none">
            <CircleArrowUpFill className="size-3.5 shrink-0" />
            {t('input.caps_lock_on')}
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordInputField;
